/**
 * useMailshots Hook (NEW Architecture - Generic)
 * 
 * Thin wrapper around useList for mailshots.
 * All database operations through MailshotRepository.
 * Zero Firestore imports - fully database-agnostic!
 */

import { useMemo } from 'react';
import { useList } from './useList';
import { MailshotRepository } from '../models/MailshotRepository';
import { db, functions } from '../firebase';

/**
 * Hook for fetching and managing mailshot lists
 * 
 * @param {Object} options - Filter and pagination options
 * @param {string} options.campaignId - Filter by campaign ID
 * @param {string} options.statusFilter - Filter by mailshot status ('all', 'active', 'completed')
 * @param {string} options.sortOrder - Sort order ('asc' or 'desc')
 * @param {number} options.pageSize - Number of items per page (default: 100)
 * @param {boolean} options.realtime - Enable real-time updates (default: true)
 * 
 * @returns {Object} { mailshots, loading, error, refresh, count, isEmpty }
 */
export const useMailshots = (options = {}) => {
  const {
    campaignId = null,
    statusFilter = 'all',
    sortOrder = 'desc',
    pageSize = 100,
    realtime = true
  } = options;

  // Create repository instance
  const repository = useMemo(() => new MailshotRepository(db, functions), []);

  // Build query options for repository
  const queryOptions = useMemo(() => {
    const whereConstraints = [];

    // Campaign filter
    if (campaignId) {
      whereConstraints.push(['campaignId', '==', campaignId]);
    }

    // Status filter
    if (statusFilter !== 'all') {
      whereConstraints.push(['status', '==', statusFilter]);
    }

    return {
      where: whereConstraints.length > 0 ? whereConstraints : undefined,
      orderBy: 'createdAt',
      orderDirection: sortOrder,
      limit: pageSize
    };
  }, [campaignId, statusFilter, sortOrder, pageSize]);

  // Use generic list hook
  const {
    entities: mailshots,
    loading,
    error,
    refresh,
    count,
    isEmpty
  } = useList({
    repository,
    options: queryOptions,
    realtime,
    immediate: true
  });

  return {
    mailshots,
    loading,
    error,
    refresh,
    count,
    isEmpty,
    hasError: !!error
  };
};
